import { useCallback } from 'react';

import useAxios from '@core/hooks/useFetch';
import { downloadExport } from '@core/utils/file';

const GET_EXPORT_LIVE_SURVEY = '/api/Survey/GetReport';
const GET_SURVEY_RESPONDENTS_INFO = '/api/Survey/GetSurveyRespondetsInfo';
const GET_CLOSED_SURVEY_RESPONDENTS_INFO = '/api/Survey/GetClosedSurveyRespondetsInfo';


export function useExportLiveSurveyXls() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<never>({
        url: GET_EXPORT_LIVE_SURVEY,
        lazy: true,
        initialConfig: {
            maxBodyLength: Infinity,
            responseType: 'arraybuffer',
        },
    });

    const doFetchAndDownload = useCallback(() => {
        doFetch().then((resp) => {
            downloadExport(resp, 'SurveysReport.xlsx');
        });
    }, [doFetch]);

    return { data, isLoading, error, doFetchAndDownload } as const;
}

const useLiveSurveyDownload = (providerId: number) => {
    const {
        isLoading,
        doFetch
    } = useAxios<never>({
        url: GET_SURVEY_RESPONDENTS_INFO,
        lazy: true,
        initialConfig: {
            maxBodyLength: Infinity,
            responseType: 'arraybuffer',
            params: {
                sampleProviderId: providerId,
            }
        },
    });

    const doFetchAndDownload = useCallback(() => {
        return doFetch().then((resp) => {
            downloadExport(resp, `LiveSurveysReport-Provider${providerId}.xlsx`);
        });
    }, [doFetch, providerId]);

    return { isLoading, doFetchAndDownload } as const;
};

export function useDownloadLiveSurveyReports() {
    const provider1 = useLiveSurveyDownload(1);
    const provider2 = useLiveSurveyDownload(2);
    const provider3 = useLiveSurveyDownload(3);

    const doFetchAndDownload = useCallback(() => {
        return Promise.all([
            provider1.doFetchAndDownload(),
            provider2.doFetchAndDownload(),
            provider3.doFetchAndDownload()
        ]);
    }, [provider1, provider2, provider3]);

    return { 
        isLoading: provider1.isLoading || provider2.isLoading || provider3.isLoading, 
        doFetchAndDownload 
    } as const;
}

const useClosedSurveyDownload = (surveyId: number, providerId: number) => {
    const {
        isLoading,
        doFetch
    } = useAxios<never>({
        url: GET_CLOSED_SURVEY_RESPONDENTS_INFO,
        lazy: true,
        initialConfig: {
            maxBodyLength: Infinity,
            responseType: 'arraybuffer',
            params: {
                surveyId,
                sampleProviderId: providerId,
            }
        },
    });

    const doFetchAndDownload = useCallback(() => {
        return doFetch().then((resp) => {
            downloadExport(resp, `ClosedSurveyReport-${surveyId}-Provider${providerId}.xlsx`);
        });
    }, [doFetch, surveyId, providerId]);

    return { isLoading, doFetchAndDownload } as const;
};

export function useDownloadClosedSurveyReports(surveyId: number) {
    const provider1 = useClosedSurveyDownload(surveyId, 1);
    const provider2 = useClosedSurveyDownload(surveyId, 2);
    const provider3 = useClosedSurveyDownload(surveyId, 3);

    const doFetchAndDownload = useCallback(() => {
        return Promise.all([
            provider1.doFetchAndDownload(),
            provider2.doFetchAndDownload(),
            provider3.doFetchAndDownload()
        ]);
    }, [provider1, provider2, provider3]);

    return { 
        isLoading: provider1.isLoading || provider2.isLoading || provider3.isLoading, 
        doFetchAndDownload 
    } as const;
}
